import { LogOrigin } from '../../Logger/LogOrigin';
import { Logger } from '../../Logger/Logger';
import { Immutable } from '../../Types';

export enum FBContextTypes {
    Solo = "SOLO",
    Group = "GROUP",
    Thread = "THREAD",
    Post = "POST"
}

export interface IContextCache {
    users: string[];
    id: string;
    type: FBContextTypes;
}

export class FBContextHandler {
    private static _cache: IContextCache = null;
    private static _isInitialized: boolean = false;

    private static _getID: () => string;
    private static _getPlayersAsync: () => Promise<Array<{getID: () => string}>>;
    private static _getContextType: () => FBContextTypes;
    private static _chooseAsync: () => Promise<void>;
    private static _createAsync: (playerID: string) => Promise<void>;

    constructor() {}

/*
    ====================================================
    =                 Important methods                =
    ====================================================
*/

    public static isInitialized(): boolean {
        return FBContextHandler._isInitialized;
    }

    public static async initialize(
        _getID: typeof FBContextHandler._getID,
        _getPlayersAsync: typeof FBContextHandler._getPlayersAsync,
        _getContextType: typeof FBContextHandler._getContextType,
        _chooseAsync: typeof FBContextHandler._chooseAsync,
        _createAsync: typeof FBContextHandler._createAsync
    ): Promise<void> {
        Logger.log("Initializing...", LogOrigin.FB_CTX_HANDLER);

        FBContextHandler._getPlayersAsync = _getPlayersAsync;
        FBContextHandler._getID = _getID;
        FBContextHandler._getContextType = _getContextType;
        FBContextHandler._chooseAsync = _chooseAsync;
        FBContextHandler._createAsync = _createAsync;

        FBContextHandler._cache = FBContextHandler._createContextCacheObject(
            FBContextHandler._getID(),
            FBContextHandler._getContextType(),
            await FBContextHandler._getPlayers(),
        );

        FBContextHandler._isInitialized = true;
        Logger.log("Initialized successfully.", LogOrigin.FB_CTX_HANDLER);
        return;
    }

    public static async chooseAsync(): Promise<void> {
        Logger.log("chooseAsync() called", LogOrigin.FB_CTX_HANDLER);
        await FBContextHandler._chooseAsync();

        await FBContextHandler.setNewContext();

        Logger.log("chooseAsync() success", LogOrigin.FB_CTX_HANDLER);

        return;
    }

    public static async createContextAsync(_id: string): Promise<void> {
        Logger.log("createContextAsync() called", LogOrigin.FB_CTX_HANDLER);
        await FBContextHandler._createAsync(_id);
        Logger.log("createContextAsync() success", LogOrigin.FB_CTX_HANDLER);
        await FBContextHandler.setNewContext();
    }

/*
    ====================================================
    =                  Getters/Setters                 =
    ====================================================
*/

    public static getContextType(): FBContextTypes {
        if(!FBContextHandler._isInitialized) throw (FBContextHandler._generateInitError());
        return FBContextHandler._cache.type;
    }

    private static setContextType(_type: FBContextTypes): void {
        FBContextHandler._cache.type = _type;
    }

    public static getContextID(): string {
        if(!FBContextHandler._isInitialized) throw (FBContextHandler._generateInitError());
        return FBContextHandler._cache.id;
    }

    private static setContextID(_id: string): void {
        Logger.log(`Changing context ID to new ID: ${_id}`, LogOrigin.FB_CTX_HANDLER);
        FBContextHandler._cache.id = _id;
    }

    public static getPlayers(): Immutable<string[]> {
        if(!FBContextHandler._isInitialized) throw (FBContextHandler._generateInitError());
        return FBContextHandler._cache.users;
    }

    private static setPlayers(_players: string[]): void {
        FBContextHandler._cache.users = _players;
    }

/*
    ====================================================
    =                  Helper functions                =
    ====================================================
*/

    public static isPlayerInContext(_uuid: string): boolean {
        if(!FBContextHandler.isInitialized()) throw (FBContextHandler._generateInitError());

        if(FBContextHandler._cache.users.length !== 1) {
            return Boolean(FBContextHandler._cache.users.find((uuid: string) => uuid === _uuid));
        } else {
            return false;
        }
    }

/*
    ====================================================
    =                  Private methods                 =
    ====================================================
*/

    private static async setNewContext(_id?: string, _type?: FBContextTypes, _players?: string[]): Promise<void> {
        if(!_id) {
            FBContextHandler.setContextID(FBContextHandler._getID());
        } else {
            FBContextHandler.setContextID(_id);
        }

        if(_type) {
            FBContextHandler.setContextType(_type);
        } else {
            FBContextHandler.setContextType(
                FBContextHandler._getContextType()
            );
        }

        if(_players && Array.isArray(_players)) {
            FBContextHandler._cache.users = [..._players]; // copy array
        } else {
            FBContextHandler.setPlayers(
                await FBContextHandler._getPlayers()
            );
        }
    }

    private static _createContextCacheObject(_id: string, _type?: FBContextTypes, _users?: string[]): IContextCache {
        return {
            id: _id,
            type: _type || FBContextTypes.Solo,
            users: _users || []
        };
    }

    private static async _getPlayers(): Promise<string[]> {
        // if(!FBContextHandler.isInitialized()) throw (FBContextHandler._generateInitError());
        if(FBContextHandler._getContextType() === FBContextTypes.Solo) {
            return [];
        }
        const players: Array<{getID: () => string}> = await FBContextHandler._getPlayersAsync();
        return players.map((p) => {
            return p.getID();
        });
    }

    private static _generateInitError(): Error {
        return new Error("FBContextHandler is not initialized!");
    }
}
